import React, { useEffect, useRef, useState } from 'react';
import { showHintPositions, defaultCameraPosition } from '../helpers/cameraPositions';
import { logEvent } from '../../utils/experimentLog';

// アニメーションのタイミング（ms）
const CAMERA_DURATION = 2000; // カメラ移動にかける時間
const HOLD_AFTER_ARRIVAL = 1500; // 到達後、モデルを差し替えるまでの待ち時間

// 「おかしさが解消される」視点（誤りごとに定義）。
// 全モデル共通で「逆方向」（この視点から誤りモデルを見ると、対象部品が
// 『既定視点から見たモチーフ』の見えと一致する）で統一。値は投影残差の数値最適化で決定
// （対象部品の投影点間距離を最小化。既定カメラは(0,194,-461)・注視点原点）。
// ・model1 ≈1%、cube_model3 ≈4%（変形が回転でないため下限）。
// ・model4 ≈9.6%：方位角(x)を0に固定し純粋な仰角変化のみにした値
//   （2026-07-05修正。以前はx=35で方位角も動いており、斜めに視点が動って見えるバグの原因だった）。
// ・cube_model5 ≈0.2%：誤りは上面をCubeModel5.js内でΔ=+20°回転させたものなので、
//   誤りの既定カメラをΔ分だけ「下げる」（仰角を下げる）とモチーフの既定視点に一致する
//   （2026-07-05修正。以前は逆に仰角を上げていたため、視点が逆方向に動いて見えるバグの原因だった）。
export const matchGazePositions = {
  model1: { x: 0, y: 56, z: -492 },
  model4: { x: 0, y: -18, z: -496 },
  cube_model3: { x: -50, y: 193, z: -475 },
  cube_model5: { x: 0, y: 26, z: -499 },
};

// アニメーション中に吹き出しで示す「視点がどちらへずれていくか」の説明。
// 方向は「既定カメラから見た画面上の向き」（学習者が頭を動かす向き）で記述する。
// 既定視点では画面右＝ワールド-x、画面下＝仰角の低下（yの減少）にあたる。
// 「おかしさが分かる視点」の画面を視線の軸まわりに回す角度（ラジアン。未定義は0）。
// cube_model3 は真上からの画面のままだと学習者の右（ワールド-x）が画面の上に映り、
// 「視点が右方向にずれていきます」と目印の矢印の向きが食い違うため、90°回して
// 学習者の右＝画面の右・学習者の奥＝画面の上にそろえる（2026-10-19）。
export const revealedViewTiltAngles = {
  cube_model3: Math.PI / 2,
};

export const matchGazeDirections = {
  model1: '視点が下方向にずれていきます',
  model4: '視点が下方向にずれていきます',
  cube_model3: '視点が右方向にずれていきます',
  cube_model5: '視点が下方向にずれていきます',
};

export default function MatchGazeButton({
  errorGlbPath,
  errorViewerRef,
  wrongViewerRef,
  isGazeMatched,
  setIsGazeMatched,
  setShowMotifModel,
  setErrorCameraPos,
  onAnimatingChange, // アニメーション開始/終了の通知（GazeControlが方向吹き出しの表示に使う）
}) {
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef(null);
  const bubbleTimerRef = useRef(null); // 方向吹き出しはカメラ移動が終わった時点で消す（差替待ちの1.5秒は表示しない）

  // アンマウント時に保留中のタイマーを解除（タブ切替などでの不正なsetState防止）
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    };
  }, []);

  const matchGaze = matchGazePositions[errorGlbPath];
  const hintPosition = showHintPositions[errorGlbPath];

  if (!matchGaze) {
    console.warn(`MatchGazeButton: no predefined position for errorGlbPath "${errorGlbPath}"`);
    return null; // 対応しないモデルはボタンを表示しない
  }

  const handleClick = () => {
    if (isAnimating) return; // アニメーション中は無視
    const ev = errorViewerRef && errorViewerRef.current;
    const wv = wrongViewerRef && wrongViewerRef.current;

    if (!isGazeMatched) {
      // === 前進：視点合わせアニメーション ===
      logEvent('gaze_match_start', { glbPath: errorGlbPath });
      setIsAnimating(true);
      if (typeof onAnimatingChange === 'function') {
        onAnimatingChange(true);
        // 吹き出しは「視点が動いている間」だけ。到達後の待ち時間には出さない
        bubbleTimerRef.current = setTimeout(() => {
          onAnimatingChange(false);
          bubbleTimerRef.current = null;
        }, CAMERA_DURATION);
      }

      // 事前リセット（OrbitControlで動かしていた場合は既定位置へ瞬時に戻す）
      if (ev && ev.snapCameraTo) ev.snapCameraTo(defaultCameraPosition);
      if (wv) {
        if (wv.clearEye) wv.clearEye();
        if (wv.snapCameraTo && hintPosition) wv.snapCameraTo(hintPosition);
      }

      // アニメーション中はユーザー操作をロック
      if (ev && ev.setOrbitEnabled) ev.setOrbitEnabled(false);
      if (wv && wv.setOrbitEnabled) wv.setOrbitEnabled(false);

      // 1. 「誤り」カメラを2秒かけて matchGaze へ
      if (ev && ev.animateCameraTo) ev.animateCameraTo(matchGaze, CAMERA_DURATION);
      // 3. 「顕在化された視点」は画面が目と一緒に動く（2026-10-19）：目は既定視点の位置に止まって見え、
      //    3Dモデルのほうが逆向きに回る。目には動く向きの矢印が付く
      if (wv && wv.playEyeAnimation) wv.playEyeAnimation(defaultCameraPosition, matchGaze, CAMERA_DURATION);

      // 2. 到達＋1.5秒後にモデルをモチーフへ差し替えると同時に、視点を既定へ戻す。
      //    逆方向設計では「誤り@match視点」の対象部品の見えが「モチーフ@既定視点」と一致するため、
      //    差し替えと視点戻しを同一バッチで行うと再構築一発で、対象部品の見えが連続したまま
      //    画面全体がモチーフ（既定視点）へ収束する。
      //    （カメラを matchGaze に確定させたまま差し替えると「モチーフ@match視点」という
      //      設計上存在しない第3の見え方が表示されてしまう）
      //    差し替えるのは「誤り」だけで、「顕在化された視点」は誤りモデルのまま目オブジェクトが
      //    matchGaze 位置に残り続ける（GazeViewColumns が persistentEyePosition で宣言的に配置。
      //    「この位置から誤りモデルを見るとおかしさが消える」という成り立つ関係を示す。2026-10-19）。
      timerRef.current = setTimeout(() => {
        setErrorCameraPos(defaultCameraPosition);
        setShowMotifModel(true);
        setIsGazeMatched(true);
        if (ev && ev.setOrbitEnabled) ev.setOrbitEnabled(true);
        if (wv && wv.setOrbitEnabled) wv.setOrbitEnabled(true);
        setIsAnimating(false);
        timerRef.current = null;
      }, CAMERA_DURATION + HOLD_AFTER_ARRIVAL);

      return;
    }

    // === 後退：元の視点に戻す（瞬時スナップ） ===
    logEvent('gaze_reset', { glbPath: errorGlbPath });
    if (wv && wv.clearEye) wv.clearEye();
    if (ev && ev.snapCameraTo) ev.snapCameraTo(defaultCameraPosition);
    if (wv && wv.snapCameraTo && hintPosition) wv.snapCameraTo(hintPosition);
    // 先にカメラ位置を default に確定（モデルはモチーフのまま、命令スナップ済みで不可視）
    setErrorCameraPos(defaultCameraPosition);
    // カメラ位置が追いついてからモデルを誤りへ戻す（既定視点への一瞬の戻りを防ぐ）
    timerRef.current = setTimeout(() => {
      setShowMotifModel(false);
      setIsGazeMatched(false);
      timerRef.current = null;
    }, 120);
  };

  const buttonLabel = isGazeMatched ? '誤りがある状態に戻す' : '視点を変えておかしさを解消する';

  // 未実行の間は点滅で「次に押すボタン」を示す
  const attention = !isGazeMatched && !isAnimating ? ' btn--attention' : '';
  return (
    <button
      onClick={handleClick}
      className={`btn btn--primary${attention}`}
      aria-label={buttonLabel}
      disabled={isAnimating}
    >{isAnimating ? '視点を移動中...' : buttonLabel}
    </button>
  );
}
